/**
 * A period-end cancellation is scheduled only while its boundary is still in the future. Keeping
 * this predicate centralized prevents a stale, elapsed `willCancelAt` mirror from making a
 * terminal membership look active or canceling indefinitely.
 */
export const hasFutureScheduledCancellation = (
  willCancelAt?: string | null,
  now: Date = new Date(),
): boolean => {
  if (willCancelAt == null) {
    return false;
  }

  const cancellationAt = new Date(willCancelAt);
  if (Number.isNaN(cancellationAt.getTime())) {
    throw new Error(`Invalid membership cancellation date: ${willCancelAt}`);
  }

  return cancellationAt > now;
};

/**
 * Answers "does this membership still have runway before its scheduled cancellation?" — the
 * expiration-blind rule the wallet and rewards UI show, deliberately different from
 * `getPromotionEligibleMembershipIds` below. During webhook convergence `canceled` can already be
 * true while `willCancelAt` still carries that schedule, so consumers must not treat the canceled
 * mirror alone as terminal.
 */
export const hasCancellationRunwayAt = (
  membership: {
    canceled?: boolean | null;
    willCancelAt?: string | null;
  },
  now: Date,
): boolean => !membership.canceled || hasFutureScheduledCancellation(membership.willCancelAt, now);

export const hasCancellationRunway = (membership: {
  canceled?: boolean | null;
  willCancelAt?: string | null;
}): boolean => hasCancellationRunwayAt(membership, new Date());

/**
 * Membership ids this person currently holds, by the same rule the backend applies when it decides
 * which membership-gated offers are claimable — `GetActiveMembershipIdsForPlatformUserAsync` in
 * `Shared/RepeatMD.Shared/Services/PatientServices/Implementations/PromotionService.cs`: not
 * canceled and not past expiration. Rows closed by an applied upgrade/downgrade are already
 * filtered out server-side, so they never reach this list. Keep the two in step — a divergence
 * shows up as an offer the app promises and checkout refuses.
 *
 * Deliberately NOT `hasCancellationRunway`: that rule answers the cancellation-runway question and
 * ignores `expiration` entirely, so it would call an elapsed membership active.
 */
export const getPromotionEligibleMembershipIds = (
  platformUserMemberships: {
    canceled: boolean;
    expiration: string;
    membership: { id: string };
  }[],
  now: Date,
): string[] => [
  ...new Set(
    platformUserMemberships
      .filter((platformUserMembership) => {
        if (platformUserMembership.canceled) return false;

        const expiresAt = new Date(platformUserMembership.expiration);
        if (Number.isNaN(expiresAt.getTime())) {
          throw new Error(
            `Invalid membership expiration date: ${platformUserMembership.expiration}`,
          );
        }

        return expiresAt >= now;
      })
      .map((platformUserMembership) => platformUserMembership.membership.id),
  ),
];
